import { useState, useEffect } from "react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import {
  Settings, Shield, Radio, Send, Loader2, KeyRound, Link as LinkIcon, MessageSquare, Save, Trash2,
  Users, CheckCircle2, XCircle, Globe, AlertTriangle, Ban, Flag, RotateCcw, Eye, FileText, UserCheck, RefreshCw, MailCheck, Swords, GitCommit, Mail, ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiFetch";
import { formatDistanceToNow } from "date-fns";

interface ResetToken {
  id: number; token: string; username: string; email: string;
  expires_at: string; created_at: string;
}

export default function AdminPanel() {
  const { data: users, refetch } = useQuery<any[]>({
    queryKey: ["all-users-admin"],
    queryFn: () => apiFetch("/users"),
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) =>
      apiFetch(`/users?path=${id}/role`, { method: "PATCH", body: JSON.stringify({ role }) }),
  });

  const { toast } = useToast();
  const [tab, setTab] = useState<"roster" | "broadcast" | "resets" | "motd" | "groups" | "aar_flags" | "pending_verify" | "disputes" | "github">("roster");
  const [bSubject, setBSubject] = useState("");
  const [bBody, setBBody] = useState("");

  const broadcastMutation = useMutation({
    mutationFn: () =>
      apiFetch("/admin?path=broadcast", { method: "POST", body: JSON.stringify({ subject: bSubject, body: bBody }) }),
    onSuccess: (data: any) => {
      toast({ title: "Broadcast Sent", description: `Message delivered to ${data.sent} member(s).` });
      setBSubject(""); setBBody("");
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const { data: resetTokens = [] } = useQuery<ResetToken[]>({
    queryKey: ["reset-tokens"],
    queryFn: () => apiFetch<ResetToken[]>("/admin?path=reset-tokens"),
    enabled: tab === "resets",
  });

  const handleRoleChange = (id: number, newRole: string) => {
    updateRole.mutate({ id, role: newRole }, {
      onSuccess: () => { toast({ title: "Role Updated" }); refetch(); },
      onError: (err: any) => toast({ title: "Update Failed", description: err.message || "Error.", variant: "destructive" }),
    });
  };

  const copyResetLink = (token: string) => {
    const url = `${window.location.origin}/portal/reset-password?token=${token}`;
    navigator.clipboard.writeText(url).then(() => toast({ title: "Reset link copied to clipboard." }));
  };

  const NAV_ITEMS = [
    { key: "roster",         label: "Personnel",       icon: <Shield className="w-4 h-4" />,      desc: "User accounts & roles" },
    { key: "broadcast",      label: "Broadcast",       icon: <Radio className="w-4 h-4" />,       desc: "System-wide messages" },
    { key: "resets",         label: "Password Resets", icon: <KeyRound className="w-4 h-4" />,    desc: "Pending reset tokens" },
    { key: "motd",           label: "MOTD / SITRAP",   icon: <MessageSquare className="w-4 h-4" />, desc: "Message of the day" },
    { key: "groups",         label: "MilSim Groups",   icon: <Globe className="w-4 h-4" />,       desc: "Group management" },
    { key: "aar_flags",      label: "AAR Flags",       icon: <Flag className="w-4 h-4" />,        desc: "Flagged after-action reports" },
    { key: "pending_verify", label: "Pending Verify",  icon: <UserCheck className="w-4 h-4" />,   desc: "Groups awaiting verification" },
    { key: "disputes",       label: "Op Disputes",     icon: <Swords className="w-4 h-4" />,      desc: "Disputed joint operations" },
    { key: "github",         label: "Deploy Feed",     icon: <GitCommit className="w-4 h-4" />,    desc: "Recent commits & deployments" },
  ];
  const activeNav = NAV_ITEMS.find(n => n.key === tab);

  return (
    <PortalLayout requireRole={["admin"]}>
      <div className="flex flex-col gap-0 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 border-b border-border pb-5 mb-0">
          <div className="w-11 h-11 bg-destructive/20 text-destructive rounded flex items-center justify-center shrink-0">
            <Settings className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold uppercase tracking-wider text-foreground">Command Console</h1>
            <p className="text-xs text-muted-foreground font-sans">Full administrative override privileges.</p>
          </div>
        </div>

        {/* Sidebar + content layout */}
        <div className="flex gap-0 flex-1 pt-5">
          {/* Sidebar */}
          <aside className="w-56 shrink-0 border-r border-border pr-4 space-y-0.5 mr-6">
            <p className="text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground/50 px-3 pb-2">Navigation</p>
            {NAV_ITEMS.map(item => (
              <button key={item.key} onClick={() => setTab(item.key as any)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded text-left transition-colors group ${
                  tab === item.key
                    ? "bg-destructive/10 border border-destructive/20 text-destructive"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-transparent"
                }`}>
                <span className={`shrink-0 ${tab === item.key ? "text-destructive" : "text-muted-foreground group-hover:text-foreground"}`}>{item.icon}</span>
                <span className="font-display font-bold uppercase tracking-widest text-[11px] leading-tight">{item.label}</span>
              </button>
            ))}
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Section header */}
            {activeNav && (
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <span className="text-destructive">{activeNav.icon}</span>
                <div>
                  <h2 className="font-display font-bold uppercase tracking-widest text-base text-foreground">{activeNav.label}</h2>
                  <p className="text-[10px] text-muted-foreground font-sans">{activeNav.desc}</p>
                </div>
              </div>
            )}

        {tab === "roster" && (
          <div className="bg-card border border-border rounded overflow-hidden shadow-lg">
            <div className="p-6 border-b border-border bg-secondary/30 flex items-center gap-3">
              <Shield className="w-5 h-5 text-destructive" />
              <h2 className="font-display font-bold uppercase tracking-wider text-lg">Global Personnel Registry</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left font-sans text-sm">
                <thead className="bg-secondary/50 font-display font-bold uppercase tracking-wider text-muted-foreground text-xs">
                  <tr>
                    <th className="px-6 py-4">ID</th>
                    <th className="px-6 py-4">Username</th>
                    <th className="px-6 py-4">Email</th>
                    <th className="px-6 py-4">Joined</th>
                    <th className="px-6 py-4 text-right">Clearance (Role)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users?.map((u: any) => (
                    <tr key={u.id} className="hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-4 text-muted-foreground">#{u.id}</td>
                      <td className="px-6 py-4 font-bold text-foreground">
                        {u.status === "suspended" && <span className="w-2 h-2 rounded-full bg-destructive inline-block mr-2" title="Suspended" />}
                        {u.username}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">{u.email}</td>
                      <td className="px-6 py-4 text-muted-foreground">{u.createdAt ? format(new Date(u.createdAt), "MMM dd, yyyy") : "—"}</td>
                      <td className="px-6 py-4 text-right">
                        <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}
                          disabled={updateRole.isPending}
                          className={`bg-background border rounded px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 ${u.role === "admin" ? "border-destructive text-destructive" : u.role === "moderator" ? "border-accent text-accent" : u.role === "staff" ? "border-blue-400 text-blue-400" : "border-border text-muted-foreground"}`}>
                          <option value="member">Member</option>
                          <option value="staff">Staff</option>
                          <option value="moderator">Moderator</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "broadcast" && (
          <div className="bg-card border border-border rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-border bg-secondary/30 flex items-center gap-3">
              <Radio className="w-5 h-5 text-primary" />
              <div>
                <h2 className="font-display font-bold uppercase tracking-wider text-lg">Broadcast Transmission</h2>
                <p className="text-xs text-muted-foreground font-sans mt-0.5">Send a message to all active members' inboxes.</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Subject</label>
                <input value={bSubject} onChange={e => setBSubject(e.target.value)} maxLength={200} className="w-full bg-background border-2 border-border rounded px-4 py-3 text-foreground font-sans focus:outline-none focus:border-primary transition-all" placeholder="System-wide announcement..." />
              </div>
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Body</label>
                <textarea value={bBody} onChange={e => setBBody(e.target.value)} rows={6} maxLength={5000} className="w-full bg-background border-2 border-border rounded px-4 py-3 text-foreground font-sans focus:outline-none focus:border-primary transition-all resize-none" placeholder="Compose your announcement here..." />
                <p className="text-xs text-muted-foreground mt-1 text-right">{bBody.length}/5000</p>
              </div>
              <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm text-muted-foreground">
                ⚠ This will send a message to <strong>all active members</strong>. Confirm before sending.
              </div>
              <div className="flex justify-end">
                <button onClick={() => broadcastMutation.mutate()} disabled={!bSubject.trim() || !bBody.trim() || broadcastMutation.isPending}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm rounded disabled:opacity-50 transition-all">
                  {broadcastMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Broadcast
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "resets" && (
          <div className="bg-card border border-border rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-border bg-secondary/30 flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-amber-400" />
              <div>
                <h2 className="font-display font-bold uppercase tracking-wider text-lg">Pending Password Resets</h2>
                <p className="text-xs text-muted-foreground font-sans mt-0.5">Copy the reset link and send to the user via Discord DM.</p>
              </div>
            </div>
            <div className="p-6">
              {resetTokens.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <KeyRound className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-display uppercase tracking-widest">No pending resets</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {resetTokens.map((token: ResetToken) => (
                    <div key={token.id} className="flex items-center justify-between gap-4 p-4 bg-secondary/40 border border-border rounded-lg">
                      <div>
                        <p className="font-display font-bold text-foreground">{token.username}</p>
                        <p className="text-xs text-muted-foreground">{token.email} · Expires {token.expires_at && !isNaN(new Date(token.expires_at).getTime()) ? format(new Date(token.expires_at), "HH:mm, MMM d") : "—"}</p>
                      </div>
                      <button onClick={() => copyResetLink(token.token)}
                        className="flex items-center gap-2 px-3 py-2 border border-border rounded text-sm font-display uppercase tracking-wider text-muted-foreground hover:text-foreground hover:border-primary transition-colors">
                        <LinkIcon className="w-4 h-4" /> Copy Link
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "motd" && <MotdTab toast={toast} />}
        {tab === "groups" && <GroupsTab toast={toast} />}
        {tab === "aar_flags" && <AarFlagsTab toast={toast} />}
        {tab === "pending_verify" && <PendingVerifyTab toast={toast} />}
        {tab === "disputes" && <OpDisputesTab toast={toast} />}
        {tab === "github" && <DeployFeedTab />}
          </div>{/* end main content */}
        </div>{/* end sidebar+content */}
      </div>{/* end outer */}
    </PortalLayout>
  );
}

function MotdTab({ toast }: { toast: any }) {
  const [motds, setMotds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", content: "", type: "info", expires_at: "" });
  const [saving, setSaving] = useState(false);

  const TYPE_COLORS: Record<string, string> = {
    info: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    warning: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    success: "text-green-400 bg-green-500/10 border-green-500/30",
    critical: "text-red-400 bg-red-500/10 border-red-500/30",
  };

  const load = () => {
    apiFetch<any[]>("/motd").then(setMotds).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      await apiFetch("/motd", { method: "POST", body: JSON.stringify({ ...form, expires_at: form.expires_at || null }) });
      toast({ title: "MOTD Posted" });
      setForm({ title: "", content: "", type: "info", expires_at: "" });
      load();
    } catch (e: any) { toast({ title: "Failed", description: e.message, variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    try {
      await apiFetch(`/motd?path=${id}`, { method: "DELETE" });
      toast({ title: "MOTD deleted" }); load();
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          <h2 className="font-display font-bold uppercase tracking-widest text-lg">Post MOTD / SITRAP</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="w-full bg-background border-2 border-border rounded px-4 py-3 text-foreground font-sans focus:outline-none focus:border-primary transition-all" placeholder="Weekly SITRAP" />
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Type</label>
            <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className="w-full bg-background border-2 border-border rounded px-4 py-3 text-foreground font-sans focus:outline-none focus:border-primary transition-all">
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Content *</label>
          <textarea rows={5} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} className="w-full bg-background border-2 border-border rounded px-4 py-3 text-foreground font-sans focus:outline-none focus:border-primary transition-all resize-none" placeholder="This week's situation report..." />
        </div>
        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Expires At (optional)</label>
          <input type="datetime-local" value={form.expires_at} onChange={e => setForm(f => ({...f, expires_at: e.target.value}))} className="w-full bg-background border-2 border-border rounded px-4 py-3 text-foreground font-sans focus:outline-none focus:border-primary transition-all" />
        </div>
        <button onClick={submit} disabled={saving || !form.title.trim() || !form.content.trim()}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm rounded disabled:opacity-50 transition-all">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Post MOTD
        </button>
      </div>

      <div className="space-y-3">
        <h3 className="font-display font-bold uppercase tracking-widest text-xs text-muted-foreground">Active MOTDs</h3>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> :
          motds.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-border rounded-lg text-muted-foreground">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="font-display text-sm uppercase tracking-widest">No MOTDs posted</p>
            </div>
          ) : (
            motds.map((m: any) => (
              <div key={m.id} className={`flex items-start justify-between gap-4 p-4 border rounded-lg ${TYPE_COLORS[m.type] ?? "text-foreground bg-card border-border"}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold uppercase tracking-wider text-sm">{m.title}</p>
                  <p className="text-xs mt-1 opacity-80 font-sans">{m.content.slice(0, 120)}{m.content.length > 120 ? "..." : ""}</p>
                  <p className="text-xs opacity-60 mt-1 font-mono">{(m.type ?? "").toUpperCase()} · {format(new Date(m.created_at), "MMM dd HH:mm")}{m.expires_at ? ` · Expires ${format(new Date(m.expires_at), "MMM dd HH:mm")}` : ""}</p>
                </div>
                <button onClick={() => remove(m.id)} className="p-1.5 opacity-60 hover:opacity-100 transition-opacity shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))
          )
        }
      </div>
    </div>
  );
}

function GroupsTab({ toast }: { toast: any }) {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "active" | "suspended">("pending");
  const [working, setWorking] = useState<string | null>(null);

  const STATUS_STYLES: Record<string, string> = {
    pending:   "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    active:    "bg-green-500/10 text-green-400 border-green-500/30",
    suspended: "bg-red-500/10 text-red-400 border-red-500/30",
    rejected:  "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  };

  const load = () => {
    setLoading(true);
    apiFetch<any[]>("/admin?path=milsim-groups")
      .then(setGroups)
      .catch(() => toast({ title: "Failed to load groups", variant: "destructive" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id: string, status: string) => {
    setWorking(id);
    try {
      await apiFetch(`/admin?path=milsim-groups/${id}/status`, {
        method: "PATCH", body: JSON.stringify({ status }),
      });
      setGroups(prev => prev.map(g => g.id === id ? { ...g, status } : g));
      toast({ title: `Group ${status}` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setWorking(null); }
  };

  const deleteGroup = async (id: string, name: string) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    setWorking(id);
    try {
      await apiFetch(`/admin?path=milsim-groups/${id}`, { method: "DELETE" });
      setGroups(prev => prev.filter(g => g.id !== id));
      toast({ title: "Group deleted" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setWorking(null); }
  };

  const toggleVerifyOverride = async (group: any) => {
    const newVal = !group.verify_override;
    setWorking(group.id);
    try {
      await apiFetch(`/admin?path=milsim-groups/${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({ verify_override: newVal, is_verified: newVal, verified_at: newVal ? new Date().toISOString() : null }),
      });
      setGroups(prev => prev.map(g => g.id === group.id ? { ...g, verify_override: newVal, is_verified: newVal } : g));
      toast({ title: newVal ? "✅ Verification Granted" : "Verification Removed", description: group.name });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setWorking(null); }
  };

  const runAutoVerify = async () => {
    setWorking("__running__");
    try {
      const token = localStorage.getItem("tag_auth_token") ?? "";
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ""}/functions/autoVerifyGroups`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      toast({ title: `Auto-Verify Complete`, description: `${data.newly_verified ?? 0} newly verified, ${data.newly_unverified ?? 0} removed. ${data.processed ?? 0} checked.` });
      // Reload groups
      apiFetch<any[]>("/admin?path=milsim-groups").then(d => setGroups(Array.isArray(d) ? d : []));
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setWorking(null); }
  };

  const filtered = groups.filter(g => filter === "all" || g.status === filter);
  const pendingCount = groups.filter(g => g.status === "pending").length;

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-lg">
        <div className="p-5 border-b border-border bg-secondary/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-display font-bold uppercase tracking-wider text-lg">MilSim Groups</h2>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">{groups.length} total · {pendingCount} pending approval</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runAutoVerify} disabled={working === "__running__"}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 rounded text-xs font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
              {working === "__running__" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Run Auto-Verify
            </button>
            <div className="flex gap-1">
            {(["pending","active","suspended","all"] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest rounded transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {f}{f === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
              </button>
            ))}
          </div></div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Globe className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-display uppercase tracking-widest text-sm">No groups in this category</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map(g => (
              <div key={g.id} className="flex items-start gap-4 p-5 hover:bg-secondary/10 transition-colors">
                {/* Logo */}
                <div className="w-12 h-12 rounded bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                  {g.logo_url ? <img src={g.logo_url} alt="" className="w-full h-full object-cover" /> : <Shield className="w-6 h-6 text-muted-foreground" />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-display font-bold text-foreground">{g.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLES[g.status] ?? "bg-card border-border text-muted-foreground"}`}>{g.status}</span>
                    {g.slug && <span className="text-xs text-muted-foreground font-mono">/{g.slug}</span>}
                  </div>
                  {g.tag_line && <p className="text-sm text-muted-foreground truncate">{g.tag_line}</p>}
                  <div className="flex gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span>Owner: {g.owner_username ?? g.owner_id}</span>
                    {g.discord_url && <a href={g.discord_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Discord ↗</a>}
                    {g.website_url && <a href={g.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Website ↗</a>}
                    {g.is_verified && <span className="text-[10px] text-cyan-400 border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 rounded font-bold uppercase tracking-widest">✓ Verified{g.verify_override ? " (override)" : ""}</span>}
                    {!g.is_verified && g.verification_score !== undefined && <span className="text-[10px] text-muted-foreground border border-border px-1.5 py-0.5 rounded">Score: {g.verification_score}/100</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => toggleVerifyOverride(g)} disabled={working === g.id}
                    title={g.verify_override ? "Remove verification override" : "Force verify this group"}
                    className={`p-1.5 rounded transition-colors disabled:opacity-50 ${g.verify_override ? "text-cyan-400 hover:text-cyan-300" : "text-muted-foreground hover:text-cyan-400"}`}>
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  {g.status !== "active" && (
                    <button onClick={() => setStatus(g.id, "active")} disabled={working === g.id}
                      title="Approve" className="flex items-center gap-1.5 px-3 py-1.5 bg-green-900/40 hover:bg-green-700/40 text-green-400 border border-green-700/40 rounded text-xs font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
                      {working === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve
                    </button>
                  )}
                  {g.status !== "suspended" && (
                    <button onClick={() => setStatus(g.id, "suspended")} disabled={working === g.id}
                      title="Suspend" className="flex items-center gap-1.5 px-3 py-1.5 bg-red-900/30 hover:bg-red-800/40 text-red-400 border border-red-700/40 rounded text-xs font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
                      {working === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Suspend
                    </button>
                  )}
                  <button onClick={() => deleteGroup(g.id, g.name)} disabled={working === g.id}
                    title="Delete" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AarFlagsTab({ toast }: { toast: any }) {
  const [aars, setAars] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [filter, setFilter] = useState<"flagged" | "all">("flagged");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    apiFetch<any[]>("/milsimAars?admin=1")
      .then(data => setAars(Array.isArray(data) ? data : []))
      .catch(() => setAars([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const clearFlag = async (aar: any) => {
    setWorking(aar.id);
    try {
      await apiFetch(`/milsimAars?path=${aar.id}/unflag`, { method: "PATCH" });
      toast({ title: "Flag Cleared", description: `AAR "${aar.title}" cleared and will now count toward leaderboard.` });
      load();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setWorking(null);
    }
  };

  const confirmFlag = async (aar: any) => {
    setWorking(aar.id);
    try {
      await apiFetch(`/milsimAars?path=${aar.id}/flag`, {
        method: "PATCH",
        body: JSON.stringify({ reason: "Manually confirmed by admin" }),
      });
      toast({ title: "Flag Confirmed", description: `AAR "${aar.title}" confirmed as invalid.` });
      load();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setWorking(null);
    }
  };

  const displayed = filter === "flagged" ? aars.filter(a => a.lb_flagged) : aars;
  const flaggedCount = aars.filter(a => a.lb_flagged).length;

  const outcomeColor: Record<string, string> = {
    victory: "text-green-400 border-green-500/30 bg-green-500/10",
    defeat: "text-red-400 border-red-500/30 bg-red-500/10",
    draw: "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    incomplete: "text-muted-foreground border-border bg-secondary",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded overflow-hidden shadow-lg">
        <div className="p-6 border-b border-border bg-secondary/30 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Flag className="w-5 h-5 text-destructive" />
            <div>
              <h2 className="font-display font-bold uppercase tracking-wider text-lg">AAR Integrity Review</h2>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">AARs auto-flagged by the anti-cheat system. Review and override below.</p>
            </div>
          </div>
          {flaggedCount > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-destructive bg-destructive/10 border border-destructive/30 px-3 py-1.5 rounded">
              <AlertTriangle className="w-3.5 h-3.5" /> {flaggedCount} Pending Review
            </span>
          )}
        </div>

        {/* Anti-cheat rules reference */}
        <div className="px-6 py-4 border-b border-border bg-secondary/10">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Anti-Cheat Rules Active</p>
          <div className="flex flex-wrap gap-3">
            {[
              { icon: <Users className="w-3 h-3" />, label: "Min 3 participants per AAR" },
              { icon: <AlertTriangle className="w-3 h-3" />, label: "Max 1 scoring AAR per group per day" },
              { icon: <Flag className="w-3 h-3" />, label: "Max 3 victories per 7-day window" },
              { icon: <CheckCircle2 className="w-3 h-3" />, label: "Op Success LB: Verified groups only" },
            ].map((rule, i) => (
              <span key={i} className="flex items-center gap-1.5 text-[10px] font-sans text-muted-foreground bg-secondary border border-border px-2.5 py-1 rounded">
                {rule.icon} {rule.label}
              </span>
            ))}
          </div>
        </div>

        {/* Filter toggle */}
        <div className="px-6 py-3 border-b border-border flex items-center gap-3">
          <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Show:</span>
          {(["flagged", "all"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded text-xs font-display font-bold uppercase tracking-wider transition-colors border ${
                filter === f ? "bg-primary/10 border-primary/40 text-primary" : "border-border text-muted-foreground hover:text-foreground"
              }`}>
              {f === "flagged" ? `Flagged (${flaggedCount})` : `All AARs (${aars.length})`}
            </button>
          ))}
          <button onClick={load} className="ml-auto p-1.5 border border-border rounded text-muted-foreground hover:text-foreground transition-colors">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-16">
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3 opacity-60" />
            <p className="font-display font-bold uppercase tracking-wider text-muted-foreground">
              {filter === "flagged" ? "No flagged AARs — system is clean" : "No AARs filed yet"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {displayed.map(aar => (
              <div key={aar.id} className={`px-6 py-4 transition-colors ${aar.lb_flagged ? "bg-destructive/5" : ""}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  {/* Status indicator */}
                  <div className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${aar.lb_flagged ? "bg-destructive" : "bg-green-400"}`} />

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-display font-bold text-sm text-foreground">{aar.title || "Untitled AAR"}</span>
                      {aar.outcome && (
                        <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${outcomeColor[aar.outcome] ?? outcomeColor.incomplete}`}>
                          {aar.outcome}
                        </span>
                      )}
                      {aar.lb_flagged && (
                        <span className="flex items-center gap-1 text-[9px] font-display font-bold uppercase tracking-widest text-destructive bg-destructive/10 border border-destructive/30 px-1.5 py-0.5 rounded">
                          <Flag className="w-2.5 h-2.5" /> LB Excluded
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-[10px] font-sans text-muted-foreground">
                      <span>By <strong className="text-foreground">{aar.author_username ?? "Unknown"}</strong></span>
                      {aar.op_name && <span>Op: <strong className="text-foreground">{aar.op_name}</strong></span>}
                      <span>{Array.isArray(aar.participants) ? aar.participants.length : 0} participants</span>
                      {aar.created_date && <span>{format(new Date(aar.created_date), "dd MMM yyyy HH:mm")}</span>}
                    </div>
                    {aar.lb_flagged && aar.lb_flag_reason && (
                      <div className="mt-2 flex items-start gap-1.5 text-[10px] font-sans text-destructive bg-destructive/10 border border-destructive/20 rounded px-2.5 py-1.5">
                        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span><strong>Flag reason:</strong> {aar.lb_flag_reason}</span>
                      </div>
                    )}

                    {/* Expanded content */}
                    {expandedId === aar.id && (
                      <div className="mt-3 space-y-2 text-xs font-sans text-muted-foreground bg-secondary/50 rounded p-3 border border-border">
                        {aar.content && (
                          <div>
                            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground mb-1">AAR Content</p>
                            <p className="leading-relaxed line-clamp-6">{aar.content}</p>
                          </div>
                        )}
                        {Array.isArray(aar.participants) && aar.participants.length > 0 && (
                          <div>
                            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground mb-1">Participants ({aar.participants.length})</p>
                            <p>{aar.participants.join(", ")}</p>
                          </div>
                        )}
                        {aar.lessons_learned && (
                          <div>
                            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground mb-1">Lessons Learned</p>
                            <p className="leading-relaxed">{aar.lessons_learned}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setExpandedId(expandedId === aar.id ? null : aar.id)}
                      className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border hover:border-primary/30 px-2.5 py-1.5 rounded transition-all">
                      <Eye className="w-3 h-3" /> {expandedId === aar.id ? "Hide" : "View"}
                    </button>
                    {aar.lb_flagged ? (
                      <button onClick={() => clearFlag(aar)} disabled={working === aar.id}
                        className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-green-400 bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 px-2.5 py-1.5 rounded transition-all disabled:opacity-50">
                        {working === aar.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Clear Flag
                      </button>
                    ) : (
                      <button onClick={() => confirmFlag(aar)} disabled={working === aar.id}
                        className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-destructive bg-destructive/10 border border-destructive/30 hover:bg-destructive/20 px-2.5 py-1.5 rounded transition-all disabled:opacity-50">
                        {working === aar.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
                        Flag
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PENDING VERIFICATION TAB ─────────────────────────────────────────────────
function PendingVerifyTab({ toast }: { toast: any }) {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const all = await apiFetch<any[]>("/admin?path=users");
      const stuck = (all || []).filter((u: any) =>
        u.status === "pending_verification" || u.email_verified === false
      );
      // Sort newest first
      stuck.sort((a: any, b: any) =>
        new Date(b.createdAt || b.created_at || 0).getTime() -
        new Date(a.createdAt || a.created_at || 0).getTime()
      );
      setUsers(stuck);
    } catch (e: any) {
      toast({ title: "Load failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resendVerification = async (user: any) => {
    setActionId(user.id + "_resend");
    try {
      await apiFetch("/authResendVerification", {
        method: "POST",
        body: JSON.stringify({ email: user.email }),
      });
      toast({ title: "Verification email sent", description: `New link sent to ${user.email}` });
    } catch (e: any) {
      toast({ title: "Send failed", description: e.message, variant: "destructive" });
    } finally {
      setActionId(null);
    }
  };

  const forceVerify = async (user: any) => {
    setActionId(user.id + "_verify");
    try {
      await apiFetch(`/admin?path=users/${user.id}/force-verify`, { method: "POST" });
      toast({ title: "Account verified", description: `${user.username} is now fully active.` });
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (e: any) {
      toast({ title: "Force verify failed", description: e.message, variant: "destructive" });
    } finally {
      setActionId(null);
    }
  };

  const deleteUser = async (user: any) => {
    if (!confirm(`Delete ${user.username}? This cannot be undone.`)) return;
    setActionId(user.id + "_delete");
    try {
      await apiFetch(`/admin?path=users/${user.id}`, { method: "DELETE" });
      toast({ title: "User deleted", description: `${user.username} removed.` });
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-lg">
      <div className="p-6 border-b border-border bg-secondary/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserCheck className="w-5 h-5 text-yellow-400" />
          <div>
            <h2 className="font-display font-bold uppercase tracking-wider text-lg">Pending Email Verification</h2>
            <p className="text-xs text-muted-foreground font-sans mt-0.5">
              Users who registered but haven&apos;t verified their email yet.
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 border border-border rounded text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : users.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground font-display uppercase tracking-widest text-sm">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-green-500" />
          No users stuck in verification
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left font-sans text-sm">
            <thead className="bg-secondary/50 font-display font-bold uppercase tracking-wider text-muted-foreground text-xs">
              <tr>
                <th className="px-5 py-4">Username</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Registered</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u: any) => (
                <tr key={u.id} className="hover:bg-secondary/20 transition-colors">
                  <td className="px-5 py-4 font-bold text-foreground">{u.username}</td>
                  <td className="px-5 py-4 text-muted-foreground font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-4 text-muted-foreground text-xs">
                    {u.createdAt || u.created_at ? format(new Date(u.createdAt || u.created_at), "dd MMM yyyy HH:mm") : "—"}
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                      {u.status || "unverified"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {/* Resend verification email */}
                      <button
                        onClick={() => resendVerification(u)}
                        disabled={!!actionId}
                        title="Resend verification email"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50">
                        {actionId === u.id + "_resend" ? <Loader2 className="w-3 h-3 animate-spin" /> : <MailCheck className="w-3 h-3" />}
                        Resend
                      </button>
                      {/* Force verify — bypass email, mark account active */}
                      <button
                        onClick={() => forceVerify(u)}
                        disabled={!!actionId}
                        title="Manually verify this account"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50">
                        {actionId === u.id + "_verify" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Force Verify
                      </button>
                      {/* Delete — nuke spam/fake accounts */}
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={!!actionId}
                        title="Delete this user"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50">
                        {actionId === u.id + "_delete" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 border-t border-border bg-secondary/20 text-xs text-muted-foreground font-sans">
            {users.length} user{users.length !== 1 ? "s" : ""} awaiting verification
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Op Disputes Tab ─────────────────────────────────────────────────────────
function OpDisputesTab({ toast }: { toast: any }) {
  const [ops, setOps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [resolveData, setResolveData] = useState<Record<string, { outcome: string; note: string }>>({});

  const API = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions";

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/jointOpsData?type=ops`);
      const d = await res.json();
      const disputed = (d.ops || []).filter((o: any) => o.status === "disputed" || o.disputed === true);
      setOps(disputed);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (opId: string) => {
    const data = resolveData[opId];
    if (!data?.outcome) return;
    setResolving(opId);
    try {
      const d = await apiFetch<any>(`/jointOpsAction`, {
        method: "POST",
        body: JSON.stringify({ action: "resolve_dispute", op_id: opId, outcome: data.outcome, admin_note: data.note || "" }),
      });
      if (d.error) throw new Error(d.error);
      toast({ title: "Dispute Resolved", description: `Points awarded. ΔA: ${d.deltaA}, ΔB: ${d.deltaB}` });
      load();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setResolving(null);
    }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground font-sans text-sm">Loading disputed ops...</div>;

  return (
    <div className="space-y-4 mt-4">
      <p className="text-xs text-muted-foreground font-sans">
        Ops where both sides submitted conflicting results, or tier gap triggered a flag. Resolve by picking the correct outcome.
      </p>
      {ops.length === 0 ? (
        <div className="py-12 text-center border border-dashed border-border rounded-lg">
          <Swords className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm text-muted-foreground font-sans">No disputed ops. All clear.</p>
        </div>
      ) : ops.map((op: any) => (
        <div key={op.id} className="bg-card border border-orange-500/30 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display font-bold text-foreground">{op.group_a_name}</span>
                <span className="text-muted-foreground text-xs font-sans">vs</span>
                <span className="font-display font-bold text-foreground">{op.group_b_name}</span>
              </div>
              <div className="text-xs text-muted-foreground font-sans">{op.game} · {op.op_type?.replace(/_/g, " ")} · {op.scheduled_at ? new Date(op.scheduled_at).toLocaleDateString("en-GB") : "TBC"}</div>
            </div>
            <span className="text-xs font-display font-bold uppercase tracking-widest text-orange-400 border border-orange-400/30 bg-orange-400/10 px-2.5 py-1 rounded">
              {op.status === "disputed" ? "Disputed" : "Flagged"}
            </span>
          </div>

          {op.dispute_reason && (
            <div className="bg-orange-400/5 border border-orange-400/20 rounded p-3">
              <p className="text-xs text-orange-300 font-sans">{op.dispute_reason}</p>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Admin Ruling</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "group_a_win", label: `${op.group_a_name} Win` },
                  { value: "group_b_win", label: `${op.group_b_name} Win` },
                  { value: "draw", label: "Draw" },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => setResolveData(d => ({ ...d, [op.id]: { ...d[op.id], outcome: opt.value } }))}
                    className={`text-xs font-display font-bold uppercase tracking-widest px-3 py-2 rounded border transition-colors ${
                      resolveData[op.id]?.outcome === opt.value
                        ? "bg-primary/15 text-primary border-primary/40"
                        : "bg-secondary border-border text-muted-foreground hover:border-primary/20"
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={resolveData[op.id]?.note || ""}
              onChange={e => setResolveData(d => ({ ...d, [op.id]: { ...d[op.id], note: e.target.value } }))}
              placeholder="Admin note (optional)..."
              className="mf-input w-full min-h-[60px] resize-none text-xs"
            />
            <button
              onClick={() => resolve(op.id)}
              disabled={!resolveData[op.id]?.outcome || resolving === op.id}
              className="inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 rounded px-5 py-2 transition-colors disabled:opacity-50">
              {resolving === op.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Apply Ruling & Award Points
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Deploy Feed Tab ────────────────────────────────────────────────────────────
function DeployFeedTab() {
  const [commits, setCommits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("https://api.github.com/repos/mcmullenr99-ux/Tactical-Adaptation-Hub/commits?per_page=20", {
          headers: { "Accept": "application/vnd.github+json" }
        });
        if (!res.ok) throw new Error(`GitHub API ${res.status}`);
        const data = await res.json();
        setCommits(data);
      } catch (e: any) {
        setError(e.message || "Failed to load commits");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading deploy feed...
    </div>
  );

  if (error) return (
    <div className="bg-destructive/10 border border-destructive/30 rounded p-4 text-sm text-destructive font-sans">{error}</div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-sans">Last 20 commits · mcmullenr99-ux/Tactical-Adaptation-Hub</p>
        <a href="https://github.com/mcmullenr99-ux/Tactical-Adaptation-Hub/commits/main" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-display font-bold uppercase tracking-widest text-primary hover:underline">
          View on GitHub <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="space-y-2">
        {commits.map((c: any) => {
          const sha = c.sha?.slice(0, 7) ?? "unknown";
          const msg = c.commit?.message?.split("\n")[0] ?? "No message";
          const author = c.commit?.author?.name ?? c.author?.login ?? "Unknown";
          const date = c.commit?.author?.date ? new Date(c.commit.author.date).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "";
          return (
            <div key={c.sha} className="bg-card border border-border rounded p-3 flex items-start gap-3">
              <span className="font-mono text-[10px] text-primary bg-primary/10 border border-primary/20 rounded px-1.5 py-0.5 shrink-0 mt-0.5">{sha}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-sans text-foreground truncate">{msg}</p>
                <p className="text-[10px] text-muted-foreground font-sans mt-0.5">{author} · {date}</p>
              </div>
              <a href={c.html_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-muted-foreground hover:text-primary transition-colors">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}
